import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getProducts } from "../utils/api";
import { imgSrc } from "../utils/images";

export default function Cart({ cart, setCart }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const list = await getProducts();
        if (cancelled) return;
        setProducts(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const byId = new Map((products || []).map((p) => [String(p.id), p]));
  const rawCart = Array.isArray(cart) ? cart : [];

  const cartRows = [];
  for (let i = 0; i < rawCart.length; i++) {
    const c = rawCart[i];
    const qty = Number(c?.quantity || 0);
    if (qty <= 0) continue;

    const p = byId.get(String(c?.id));
    const price = Number(p?.price || 0);
    cartRows.push({
      id: c?.id,
      quantity: qty,
      product: p || null,
      price,
      lineTotal: price * qty,
      name: p?.name || `Item #${c?.id}`,
      image: p?.image_path ?? p?.image ?? "",
      status: p?.status || "",
    });
  }

  let subtotal = 0;
  for (let i = 0; i < cartRows.length; i++)
    subtotal += Number(cartRows[i].lineTotal || 0);

  function setQty(id, qty) {
    const raw = Number(qty);
    const n = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0; //

    const list = Array.isArray(cart) ? [...cart] : [];
    const next = list.map((c) =>
      String(c.id) === String(id) ? { ...c, quantity: n } : c,
    );

    setCart(next.filter((c) => Number(c.quantity || 0) > 0));
  }

  function remove(id) {
    const next = (Array.isArray(cart) ? cart : []).filter(
      (c) => String(c.id) !== String(id),
    );
    setCart(next);
  }

  return (
    <div className="container pagePadBottom cartPage">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Your Cart</div>
          <div className="muted small">Review items and adjust quantities.</div>
        </div>

        <div className="pageHeaderRight">
          {/* <div className="pill">
            Subtotal <span className="pillStrong">${subtotal.toFixed(2)}</span>
          </div> */}
        </div>
      </div>

      {loading ? (
        <div className="muted" style={{ marginTop: 12 }}>
          Loading…
        </div>
      ) : cartRows.length === 0 ? (
        <div className="card emptyCard" style={{ marginTop: 12 }}>
          <div className="emptyTitle">Cart is empty</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Browse the shop and add items you need.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btnPrimary"
              onClick={() => navigate("/categories")}
              type="button"
            >
              Browse items
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="cartList" style={{ marginTop: 12 }}>
            {cartRows.map((r) => (
              <div key={r.id} className="cartItem">
                <div className="cartMedia">
                  <img className="cartImg" src={imgSrc(r.image)} alt={r.name} />
                </div>

                <div className="cartBody">
                  <div className="cartTop">
                    <div className="cartName" title={r.name}>
                      {r.name}
                    </div>
                    <div className="cartTotal">
                      ${Number(r.lineTotal || 0).toFixed(2)}
                    </div>
                  </div>

                  <div className="muted small" style={{ marginTop: 6 }}>
                    ${Number(r.price || 0).toFixed(2)} each
                    {!r.product ? " • (This item no longer exists)" : ""}
                  </div>

                  <div className="cartBottom">
                    <div className="qtyGroup" aria-label="Quantity controls">
                      <button
                        className="btnSecondary qtyBtn"
                        onClick={() => setQty(r.id, r.quantity - 1)}
                        type="button"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>

                      <input
                        className="input qtyInput"
                        value={String(r.quantity)}
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        onChange={(e) => setQty(r.id, e.target.value)}
                        aria-label="Quantity"
                      />

                      <button
                        className="btnSecondary qtyBtn"
                        onClick={() => setQty(r.id, r.quantity + 1)}
                        type="button"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <div className="cartActions">
                      <button
                        className="btnSecondary"
                        type="button"
                        onClick={() => remove(r.id)}
                      >
                        Remove
                      </button>
                      {r.product ? (
                        <Link className="btnSecondary" to={`/item/${r.id}`}>
                          View
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="cartFooter">
            <button
              className="btnSecondary"
              onClick={() => setCart([])}
              type="button"
            >
              Clear Cart
            </button>

            <div className="cartFooterRight">
              <div className="cartFooterTotal">
                Total: <span className="priceBlue">${subtotal.toFixed(2)}</span>
              </div>
              <Link className="btnPrimary" to="/checkout">
                Checkout
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
